// src/services/productivity.js

// ✅ استخدم نفس الـ API_BASE_URL من ملف موجود
const API_BASE_URL = 'https://new.sbdemolition.online/api';

// ============================================
// TASKS API
// ============================================

export const productivityAPI = {
  // Get all tasks
  getTasks: async (userId, filters = {}) => {
    const params = new URLSearchParams({ userId, ...filters });
    const response = await fetch(`${API_BASE_URL}/productivity/tasks/list.php?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch tasks');
    }
    
    return response.json();
  },

  // Create task
  createTask: async (taskData) => {
    const response = await fetch(`${API_BASE_URL}/productivity/tasks/create.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(taskData),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create task');
    }
    
    return response.json();
  },

  // Update task
  updateTask: async (taskId, updates) => {
    const response = await fetch(`${API_BASE_URL}/productivity/tasks/update.php?id=${taskId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update task');
    }
    
    return response.json();
  },

  // Delete task
  deleteTask: async (taskId) => {
    const response = await fetch(`${API_BASE_URL}/productivity/tasks/delete.php?id=${taskId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete task');
    }
    
    return response.json();
  },

  // ============================================
  // SESSIONS API
  // ============================================

  // Get all sessions
  getSessions: async (userId, filters = {}) => {
    const params = new URLSearchParams({ userId, ...filters });
    const response = await fetch(`${API_BASE_URL}/productivity/sessions/list.php?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch sessions');
    }
    
    return response.json();
  },

  // Create session
  createSession: async (sessionData) => {
    const response = await fetch(`${API_BASE_URL}/productivity/sessions/create.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(sessionData),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create session');
    }
    
    return response.json();
  },

  // ============================================
  // STATS & ANALYTICS
  // ============================================

  // Get daily stats (deprecated - use get-weekly-stats.php instead)
  getDailyStats: async (userId, date) => {
    try {
      const tasksRes = await productivityAPI.getTasks(userId);
      const sessionsRes = await productivityAPI.getSessions(userId, { date, type: 'focus' });
      
      const tasks = tasksRes.data || [];
      const sessions = sessionsRes.data || [];
      
      // ✅ Filter tasks completed on this specific date
      const completedTasksOnDate = tasks.filter(t => {
        if (!t.completed_at) return false;
        const completedDate = t.completed_at.split(' ')[0]; // Get YYYY-MM-DD
        return completedDate === date && t.status === 'completed';
      });
      
      return {
        totalTasks: tasks.length,
        completedTasks: completedTasksOnDate.length,
        focusMinutes: Math.round(sessions.reduce((acc, s) => acc + (s.duration || 0), 0) / 60),
        sessionsCount: sessions.length,
      };
    } catch (error) {
      console.error('getDailyStats error:', error);
      return {
        totalTasks: 0,
        completedTasks: 0,
        focusMinutes: 0,
        sessionsCount: 0,
      };
    }
  },

  // ✅ Get weekly stats from dedicated endpoint
  getWeeklyStats: async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/productivity/get-weekly-stats.php?userId=${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch weekly stats');
      }
      
      return response.json();
    } catch (error) {
      console.error('getWeeklyStats error:', error);
      return {
        success: false,
        error: error.message,
        data: {
          dailyData: [],
          totalFocus: 0,
          totalCompleted: 0,
          mostProductiveDay: 'N/A'
        }
      };
    }
  },

  // ============================================
  // ✅ USERS & ASSIGNMENTS
  // ============================================

  // Get all users for assignment dropdown
  getUsers: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/productivity/get-users.php`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch users');
      }
      
      return response.json();
    } catch (error) {
      console.error('getUsers error:', error);
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  },

  // Get tasks for a specific date (for WeeklyView modal)
  getDailyTasks: async (date) => {
    try {
      const response = await fetch(`${API_BASE_URL}/productivity/get-daily-tasks.php?date=${date}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch daily tasks');
      }
      
      return response.json();
    } catch (error) {
      console.error('getDailyTasks error:', error);
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  },

  // ✅ Get team analytics (Admin only)
  getTeamAnalytics: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/productivity/get-team-analytics.php`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch team analytics');
      }
      
      return response.json();
    } catch (error) {
      console.error('getTeamAnalytics error:', error);
      return {
        success: false,
        error: error.message,
        data: {
          users: [],
          summary: {
            totalUsers: 0,
            totalFocusTime: 0,
            totalTasksCompleted: 0,
            totalSessions: 0,
            dateRange: {
              start: '',
              end: ''
            }
          }
        }
      };
    }
  },
};
